import { z } from "zod";
import { CALL_OUTCOMES } from "./calls.js";
import { idSchema, singleLineTextSchema } from "./text.js";
// Il Power Dialer sul server (piano M5, Task 3 e 4; D1–D6): la sessione di chiamate di una persona nasce da una sorgente (una
// Smart List; da R4 una scheda dei Task), tiene un Lead aperto riservato a lei e avanza al Lead dopo rileggendo la coda, che
// non si materializza mai: è la condizione della lista, nel perimetro di chi chiama, meno i Lead già lavorati nella sessione.
// Le rotte sono `POST /api/dialer-sessions`, `GET /api/dialer-sessions/current`, `GET /api/dialer-sessions/saved?source=…`,
// `GET /api/dialer-sessions/:id`, `GET /api/dialer-sessions/:id/queue`, `POST /api/dialer-sessions/:id/advance` e, dal Task 4,
// `POST …/skip`, `POST …/pause`, `POST …/resume`, `POST …/heartbeat`, `POST …/interrupt` e `GET …/summary`.
//
// Gli elenchi qui sotto ripetono quelli del dominio e dello schema (`@gioia365/domain`: `dialer-queue.ts`;
// `@gioia365/db`: `dialer.ts`), che l'interfaccia e i contratti non importano: un test del server li confronta voce per voce.
/** Da dove nasce la sessione: una Smart List, o da R4 una scheda dei Task («Oggi», «Scaduti», con «Di chi»). */
export const DIALER_SOURCE_TYPES = ["smart_list", "task_view"];
/** `active` e `paused` sono le sessioni vive; `interrupted` è salvata per «Riprendi a chiamare»; `finished` è chiusa. */
export const DIALER_SESSION_STATES = ["active", "paused", "interrupted", "finished"];
/** I motivi di «Salta questo Lead», le quattro voci del mock-up (Task 4). */
export const DIALER_SKIP_REASONS = ["asked_later", "data_to_verify", "colleague_follows", "other"];
/**
 * Perché un Lead della coda si salta da solo, nell'ordine in cui si dicono: i motivi di «Chiama» della scheda nell'ordine
 * della regola (`callability`), poi il richiamo concordato per più tardi (`passoverReason` del dominio).
 */
export const PASSOVER_REASONS = ["status_blocks", "phone_wrong", "no_phone", "in_call", "reserved", "callback_later"];
/** Come è andato un Lead lavorato nella sessione: chiamato, saltato con «Salta», passato al successivo senza chiamare. */
export const DIALER_ITEM_RESULTS = ["called", "skipped", "passed"];
/** La sorgente: una Smart List, o (R4) la scheda «Oggi» o «Scaduti» dei Task con «Di chi» («Tutto il team» o una persona). */
export const dialerSourceSchema = z.discriminatedUnion("type", [
    z.strictObject({ type: z.literal("smart_list"), smartListId: idSchema }),
    z.strictObject({ type: z.literal("task_view"), view: z.enum(["today", "overdue"]), who: z.union([z.literal("all"), idSchema]) }),
]);
/**
 * La chiave della sorgente, con la persona la chiave della sessione salvata («una interrotta per sorgente»): `smart_list:<id>`
 * oppure `task:<scheda>:<di chi>`. È il parametro `source` di `GET /api/dialer-sessions/saved`.
 */
export function dialerSourceKey(source) {
    return source.type === "smart_list" ? `smart_list:${source.smartListId}` : `task:${source.view}:${source.who}`;
}
const ID_CHARS = "[A-Za-z0-9_-]{1,128}";
/** Una chiave di sorgente ben formata (vedi `dialerSourceKey`). */
export const DIALER_SOURCE_KEY_PATTERN = new RegExp(`^(smart_list:${ID_CHARS}|task:(today|overdue):(all|${ID_CHARS}))$`);
export const dialerSourceKeySchema = z.string().max(200).regex(DIALER_SOURCE_KEY_PATTERN);
/**
 * `POST /api/dialer-sessions`: la sorgente e il modo, scelto all'inizio (decisione del 28–29 settembre 2026): `start` («Inizia
 * a chiamare»), `resume` («Riprendi a chiamare» la sessione interrotta di quella sorgente), `restart` («Ricomincia da capo»: la
 * sessione interrotta si chiude e ne nasce una nuova). Anche `start` con una sessione interrotta della stessa sorgente
 * ricomincia da capo (ruling Q6).
 */
export const startDialerSessionBodySchema = z.strictObject({ source: dialerSourceSchema, mode: z.enum(["start", "resume", "restart"]) });
/**
 * Un Lead della cascata: il nome e il salone (come nell'elenco), il posto nell'ordine della lista (quando è stato preso,
 * oppure adesso; `null` se non è più nella lista), perché si salta da solo (con il collega al telefono o che lo ha
 * riservato; per `callback_later` anche `at`, l'ora del richiamo concordato, che i testi dicono nel fuso dell'azienda, e
 * `null` per ogni altro motivo) e, da R3, il prossimo passo.
 */
export const dialerLeadSchema = z.object({
    id: z.string(),
    displayName: z.string().nullable(),
    salonName: z.string().nullable(),
    position: z.number().int().nullable(),
    passover: z.object({ reason: z.enum(PASSOVER_REASONS), byDisplayName: z.string().nullable(), at: z.string().nullable() }).nullable(),
    nextStep: z.string().nullable(),
});
/**
 * La sessione come la vede la barra «Power Dialer»: lo stato, la sorgente con il nome e la revisione di allora, il Lead aperto
 * con la scadenza della sua riserva, se è già stato chiamato (`currentCalled`), il Lead dopo (per precaricarne la scheda), i
 * fatti e quanti ne restano, i secondi prima della chiamata e quelli dopo «Chiudi la chiamata» prima del Lead successivo, e la
 * versione dei comandi (`409 version_conflict`).
 * `attemptPending` (Task 11, D9): il Lead aperto ha un tentativo della sequenza ancora aperto, nella mezza giornata di adesso,
 * con meno chiamate di quelle richieste (il numero, le fatte, le richieste): «Lead successivo» chiede «Del tentativo {n} hai
 * fatto {made} su {required}. Decidi tu».
 * `autoRedial` (TEL-32): «Senza risposta, richiama da solo…» dell'azienda, che decide se il browser richiama senza che nessuno
 * dia l'esito; `currentCall` (TEL-32): l'ultima chiamata della sessione al Lead aperto, con l'esito di Gioia365 se c'è.
 */
export const dialerSessionViewSchema = z.object({
    id: z.string(),
    state: z.enum(DIALER_SESSION_STATES),
    source: dialerSourceSchema,
    sourceName: z.string(),
    sourceRevision: z.number().int().nullable(),
    current: dialerLeadSchema.nullable(),
    // Il Lead aperto è già stato chiamato in questa sessione (con la sessione, o da chi chiama dalla scheda dalla presa del Lead),
    // con una chiamata ancora aperta o arrivata al Lead (valida). La sessione ritrovata riparte da lì, mai da «Chiama subito» (prova
    // del 6 ottobre 2026); dopo una chiamata mai partita (il telefono del browser non caricato, il microfono negato) è `false` e il
    // Lead riparte dal conto fermo, anche se «Salta» resta `409 dialer_already_called`. `false` senza un Lead aperto.
    currentCalled: z.boolean(),
    reservedUntil: z.string().nullable(),
    next: dialerLeadSchema.nullable(),
    done: z.number().int(),
    remaining: z.number().int(),
    countdownSeconds: z.number().int(),
    // I secondi dopo «Chiudi la chiamata» prima del Lead successivo (Impostazioni → «Sequenza di chiamate», decisione del 9 ottobre
    // 2026), letti a ogni vista come `countdownSeconds`; `null` è «lo passo io»: nessun passaggio da solo.
    advanceSeconds: z.number().int().nullable(),
    // «Senza risposta, richiama da solo fino alle chiamate di fila del tentativo» (Impostazioni → «Sequenza di chiamate», decisione
    // dell'11 ottobre 2026, TEL-32), letto a ogni vista come `countdownSeconds`.
    autoRedial: z.boolean(),
    attemptPending: z.object({ attempt: z.number().int(), made: z.number().int(), required: z.number().int() }).nullable(),
    // L'ultima chiamata di questa sessione al Lead aperto (per `started_at`, poi `id`), `null` senza: se ha lasciato `in_progress`
    // (`ended`), il suo esito e se l'ha dato Gioia365 (`automatic`, TEL-32). La fine e l'esito di Gioia365 stanno nella stessa
    // transazione: finita e senza esito, non lo riceverà più. Il browser la guarda dopo una chiamata senza risposta (TEL-32).
    currentCall: z.object({ id: z.string(), ended: z.boolean(), outcome: z.enum(CALL_OUTCOMES).nullable(), automatic: z.boolean() }).nullable(),
    startedAt: z.string(),
    interruptedAt: z.string().nullable(),
    version: z.number().int(),
});
/**
 * `POST /api/dialer-sessions` (201; con `mode: "resume"`, la ripresa della sessione interrotta, 200), `GET /api/dialer-sessions/:id`,
 * `POST …/advance`, `POST …/skip`, `POST …/pause` e `POST …/resume`: la vista della sessione.
 */
export const dialerSessionResponseSchema = z.object({ session: dialerSessionViewSchema });
/** `GET /api/dialer-sessions/current`: la sessione attiva o in pausa di chi guarda, `null` se non ne ha. */
export const currentDialerSessionResponseSchema = z.object({ session: dialerSessionViewSchema.nullable() });
/**
 * «La coda della sessione»: il Lead aperto, i prossimi nell'ordine della lista con la loro posizione, quelli che si saltano da
 * soli con il motivo, quelli già fatti nella sessione con il risultato, e quanti restano da chiamare.
 */
export const dialerQueueSchema = z.object({
    current: dialerLeadSchema.nullable(),
    upcoming: z.array(dialerLeadSchema),
    passedOver: z.array(dialerLeadSchema),
    worked: z.array(z.object({ lead: dialerLeadSchema, result: z.enum(DIALER_ITEM_RESULTS), reason: z.string().nullable(), at: z.string() })),
    remaining: z.number().int(),
});
/**
 * `POST /api/dialer-sessions/:id/advance`, «Lead successivo»: la versione letta e la chiave del browser (lo stesso comando
 * ripetuto risponde la stessa vista). `passOver` (Task 11, D9): «Apri il Lead successivo» dopo «Del tentativo N hai fatto X su
 * Y. Decidi tu» chiude il tentativo della sequenza che il Lead aperto ha a metà (`attemptPending` della vista) come passato
 * oltre, con le chiamate che mancano «Non fatta»; senza un tentativo a metà non scrive nulla sulla sequenza. Di partenza no: il
 * tentativo resta aperto fino alla fine della sua mezza giornata.
 */
export const advanceDialerBodySchema = z.strictObject({ expectedVersion: z.number().int().min(1), passOver: z.boolean().default(false), requestId: idSchema });
/**
 * `POST /api/dialer-sessions/:id/skip`, «Salta questo Lead» (Task 4): la versione letta, il motivo (le quattro voci del
 * mock-up) e una nota facoltativa. Vale soltanto prima di una chiamata del Lead nella sessione (`409 dialer_already_called`);
 * risponde la vista, come `advance`. `passOver` (Task 11, D9), come per `advance`: chi salta chiude anche il tentativo della
 * sequenza che il Lead ha a metà (`attemptPending` della vista, con chiamate fatte prima che la sessione lo prendesse); di
 * partenza no, e il tentativo resta aperto fino alla fine della sua mezza giornata.
 */
export const skipDialerBodySchema = z.strictObject({
    expectedVersion: z.number().int().min(1),
    reason: z.enum(DIALER_SKIP_REASONS),
    note: singleLineTextSchema(200).nullable(),
    passOver: z.boolean().default(false),
});
/**
 * `POST …/pause`, `POST …/resume` e `POST …/interrupt` (Task 4): la versione letta (`409 version_conflict` se è vecchia). Una
 * sessione già nello stato chiesto (in pausa per la pausa, attiva per la ripresa, interrotta o finita per l'interruzione: lo
 * stesso comando ripetuto, o un'altra scheda arrivata prima) risponde com'è, senza un passo in più. Pausa e ripresa rispondono
 * la vista (200); l'interruzione il riepilogo.
 */
export const dialerCommandBodySchema = z.strictObject({ expectedVersion: z.number().int().min(1) });
/**
 * `POST …/heartbeat`, il battito del browser ogni 60 secondi (Task 4, D2 e D4), senza corpo: rinnova la sessione e la riserva
 * del Lead aperto. `reservedUntil` è la scadenza della riserva di adesso, `null` senza un Lead aperto o se la riserva è di un
 * collega.
 */
export const dialerHeartbeatResponseSchema = z.object({ reservedUntil: z.string().nullable() });
/**
 * Il riepilogo di «Sessione interrotta» e della fine della lista (Task 4, D6): Lead chiamati, chiamate, con risposta e secondi
 * al telefono dalle chiamate della sessione (`calls.dialer_session_id`, le sole valide; una risposta senza durata vale 0 s,
 * questione 254); i Lead saltati con «Salta» con il motivo e la nota, e quelli passati al successivo senza chiamare (dalle righe
 * della sessione); quelli che adesso si saltano da soli, quanti restano e da chi si ripartirebbe (dalla coda riletta); se la
 * sessione è finita.
 */
export const dialerSummarySchema = z.object({
    leadsCalled: z.number().int(),
    calls: z.number().int(),
    answered: z.number().int(),
    talkSeconds: z.number().int(),
    skippedByUser: z.array(z.object({ lead: dialerLeadSchema, reason: z.enum(DIALER_SKIP_REASONS), note: z.string().nullable() })),
    passedWithoutCall: z.array(dialerLeadSchema),
    passedOver: z.array(dialerLeadSchema),
    remaining: z.number().int(),
    finished: z.boolean(),
    next: dialerLeadSchema.nullable(),
});
/** `POST …/interrupt` e `GET …/summary`: il riepilogo della sessione. */
export const dialerSummaryResponseSchema = z.object({ summary: dialerSummarySchema });
/** La sessione interrotta di una sorgente, per «Riprendi a chiamare»: quando, quanti fatti e da chi si riparte. */
export const savedDialerSessionSchema = z.object({ id: z.string(), interruptedAt: z.string(), done: z.number().int(), next: dialerLeadSchema.nullable() });
/** Query di `GET /api/dialer-sessions/saved`: la chiave della sorgente. */
export const savedDialerSessionQuerySchema = z.strictObject({ source: dialerSourceKeySchema });
/** `GET /api/dialer-sessions/saved`: la sessione interrotta di chi guarda per quella sorgente, `null` se non c'è. */
export const savedDialerSessionResponseSchema = z.object({ saved: savedDialerSessionSchema.nullable() });
/**
 * I dettagli di `409 dialer_session_active`: la sessione viva di chi chiede, con il nome della sua sorgente, che l'interfaccia
 * offre di interrompere.
 */
export const dialerSessionActiveDetailsSchema = z.object({ sessionId: z.string(), sourceName: z.string() });
//# sourceMappingURL=dialer.js.map